import { useLingui } from '@lingui/react/macro'
import type { FimSubjectRecord } from 'features/fim/types'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo, useState } from 'react'

import { errorMessage } from './errorMessage'
import { subjectHeroEntry } from './photoEntry'
import { type PhotoElectionRequest, usePhotoService } from './photoService'
import type { FimPhotoEntry } from './types'

export interface PendingPhotoAction {
  photoId: string
  action: 'make_hero' | 'remove'
}

export function useSubjectPhotoActions(
  siteId: string,
  subject: FimSubjectRecord,
  entries: FimPhotoEntry[],
  onChanged: () => Promise<unknown>
): {
  heroId: string | undefined
  pending: PendingPhotoAction | null
  makeHero: (photoId: string) => void
  removePhoto: (photoId: string) => void
} {
  const { t } = useLingui()
  const { setHeroPhoto, removePhotoFromSubject } = usePhotoService()
  const { errorNotification } = useGeckoNotifications()
  const [pending, setPending] = useState<PendingPhotoAction | null>(null)

  const heroId = useMemo(
    () => subjectHeroEntry(subject, entries)?.id,
    [entries, subject]
  )

  const run = useCallback(
    async (
      { photoId, action }: PendingPhotoAction,
      write: (photoId: string, request: PhotoElectionRequest) => Promise<void>
    ) => {
      const entry = entries.find((candidate) => candidate.id === photoId)
      if (!entry) return
      setPending({ photoId, action })
      try {
        await write(photoId, {
          siteId,
          subject,
        })
        await onChanged()
      } catch (error) {
        const reason = errorMessage(error)
        errorNotification(
          action === 'make_hero'
            ? t`Hero photo change failed: ${reason}`
            : t`Photo remove failed: ${reason}`
        )
      } finally {
        setPending(null)
      }
    },
    [entries, errorNotification, onChanged, siteId, subject, t]
  )

  const makeHero = useCallback(
    (photoId: string) =>
      void run({ photoId, action: 'make_hero' }, setHeroPhoto),
    [run, setHeroPhoto]
  )
  const removePhoto = useCallback(
    (photoId: string) =>
      void run({ photoId, action: 'remove' }, removePhotoFromSubject),
    [removePhotoFromSubject, run]
  )

  return { heroId, pending, makeHero, removePhoto }
}
